// ./src/components/features/EventVendors/VendorChecksEditor/VendorChecksEditor.tsx
//
// ROADMAP #202 item 1 — coverage checks: what a document must show, in plain words. The reviewer ticks each before approving, and the vendor
// sees them when they upload. A short list of common ones is one tap away, and any words are allowed: what is required depends on the venue and
// the local rules. New because the request drawer, the Ask drawer and a venue's requirements all need the same list; the app has no array editor.
import React from "react";
import { ActionIcon, Button, Chip, Group, Stack, Text, TextInput, Tooltip } from "@mantine/core";

import { VENDOR_DOCUMENT_TYPE, VENDOR_LIMITS } from "@/utils/api";
import { AppIcon } from "@/components/generic";

/** A starting point, by what the document is. ⓘ Limits vary by place: the amount is there to be changed. */
const COMMON_CHECKS: Record<string, string[]> = {
  [VENDOR_DOCUMENT_TYPE.INSURANCE]: [
    "Names the event organizer as additional insured",
    "Names the venue as additional insured",
    "Liability limit of at least $2,000,000",
    "Waiver of subrogation in favour of the organizer",
    "Covers the event dates",
  ],
  [VENDOR_DOCUMENT_TYPE.PERMIT]: ["Issued for the event dates", "Issued for this venue"],
  [VENDOR_DOCUMENT_TYPE.LICENSE]: ["Valid on the event dates"],
  [VENDOR_DOCUMENT_TYPE.CERTIFICATE]: ["Valid on the event dates"],
};

/** What to save: the words typed, without the rows left empty. */
export const vendorChecksToSave = (rows: string[]): string[] => rows.map((row) => row.trim()).filter(Boolean);

export interface VendorChecksEditorProps {
  value: string[];
  onChange: (rows: string[]) => void;
  documentType: string;
  error?: string | null;
  disabled?: boolean;
}

export const VendorChecksEditor: React.FC<VendorChecksEditorProps> = ({ value, onChange, documentType, error, disabled = false }) => {
  const common = (COMMON_CHECKS[documentType] ?? []).filter((label) => !value.some((row) => row.trim().toLowerCase() === label.toLowerCase()));
  const hasRoom = value.length < VENDOR_LIMITS.CHECKS_MAX;

  return (
    <Stack gap="xs">
      <Stack gap={2}>
        <Text size="md" fw={500}>
          What it must show
        </Text>
        <Text size="md" c="dimmed">
          The vendor sees these, and whoever reviews it ticks each one before approving.
        </Text>
      </Stack>

      {value.map((row, index) => (
        <Group key={index} gap="xs" wrap="nowrap" align="flex-start">
          <TextInput
            aria-label={`Check ${index + 1}`}
            placeholder="e.g. Names the venue as additional insured"
            value={row}
            onChange={(e) => {
              const next = e.currentTarget.value;

              onChange(value.map((other, i) => (i === index ? next : other)));
            }}
            maxLength={VENDOR_LIMITS.CHECK_LABEL_MAX}
            disabled={disabled}
            size="md"
            style={{ flex: 1 }}
          />
          <Tooltip label="Remove this check" withArrow>
            <ActionIcon
              variant="subtle"
              color="error"
              size="lg"
              mt={4}
              aria-label="Remove this check"
              disabled={disabled}
              onClick={() => onChange(value.filter((_, i) => i !== index))}
            >
              <AppIcon iconName="Trash" size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      ))}

      {error ? (
        <Text size="md" c="red">
          {error}
        </Text>
      ) : null}

      {hasRoom && common.length > 0 ? (
        <Group gap="xs">
          {common.map((label) => (
            // ⚠ A chip does not wrap by itself: one of these sentences ran off a phone's screen.
            <Chip
              key={label}
              size="md"
              checked={false}
              disabled={disabled}
              onChange={() => onChange([...value, label])}
              styles={{ label: { whiteSpace: "normal", height: "auto", minHeight: "var(--chip-size)", paddingTop: 4, paddingBottom: 4 } }}
            >
              {label}
            </Chip>
          ))}
        </Group>
      ) : null}

      {hasRoom ? (
        <Group>
          <Button size="md" variant="default" disabled={disabled} leftSection={<AppIcon iconName="Plus" size={16} />} onClick={() => onChange([...value, ""])}>
            Add a check
          </Button>
        </Group>
      ) : null}
    </Stack>
  );
};
